<script setup lang="ts">
import { GEO_HUB_MIN_CITY } from "#shared/utils/geoHubThresholds";
import { nomDepuisCleVille } from "#shared/utils/villeAnnuaire";

/**
 * Page programmatique SEO : adhésions par ville (longue traîne géo).
 * Ex : /adhesions/paris — ranke sur "adhésion association paris",
 * "rejoindre association paris".
 *
 * Karim 2026-06-15 (SEO sprint v4).
 */
import { formatCurrency } from "@donoor/utils";

const { t } = useI18n({ useScope: "local" });

const config = useRuntimeConfig();
const apiUrl = config.public.apiBaseUrl;
const assetsUrl = `${apiUrl}/api/assets`;

const route = useRoute();
const villeSlug = (route.params as Record<string, string | string[] | undefined>).ville as string;

interface MembershipWithCity {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  price: number;
  duration_months: number | null;
  location_city: string | null;
  organization_id: number;
  organization_name: string;
  organization_slug: string;
  organization_logo: string | null;
  city_slug: string;
  city_name: string;
}

const { data, pending } = await useAsyncData(
  `memberships-city-${villeSlug}`,
  async () => {
    try {
      return await $fetch<{
        cities: Array<{ slug: string; name: string; count: number }>;
        memberships: MembershipWithCity[];
        cityName: string | null;
        redirection?: string | null;
      }>(`/api/memberships/by-city?city=${villeSlug}`);
    } catch {
      return { cities: [], memberships: [], cityName: null, redirection: null };
    }
  },
  { server: true, lazy: false },
);

// Ancienne clé de ville (lot 4 : suffixe d'outre-mer ajouté, commune
// renommée par la BAN) : 301 vers la nouvelle clé, même hub.
if (data.value?.redirection) {
  await navigateTo(`/adhesions/${data.value.redirection}`, { redirectCode: 301 });
}

const memberships = computed(() => data.value?.memberships || []);
const cityName = computed(() => {
  if (data.value?.cityName) return data.value.cityName;
  return nomDepuisCleVille(villeSlug);
});

const canonicalUrl = `https://donoor.org/adhesions/${villeSlug}`;

const seoTitle = computed(
  () => t('seo.title', { ville: cityName.value }),
);
const seoDescription = computed(
  () =>
    t('seo.description', {
      n: memberships.value.length,
      s: memberships.value.length > 1 ? "s" : "",
      ville: cityName.value,
    }),
);

const shouldNoindex = computed(
  () => !pending.value && memberships.value.length < GEO_HUB_MIN_CITY,
);
// robots cohérent avec l'indexabilité globale (staging = noindex) + noindex page si contenu mince.
const robotsContent = useRobotsContent(shouldNoindex);

const formatDuration = (months: number | null): string => {
  if (months == null) return "";
  if (months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? t('duree.unAn') : t('duree.ans', { annees: years });
  }
  return months === 1 ? t('duree.unMois') : t('duree.mois', { mois: months });
};

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: () => `https://donoor.org/api/og/recherche?type=adhesions&count=${memberships.value.length}`,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary_large_image",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: t('breadcrumb.accueil'), item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.adhesions'), item: "https://donoor.org/recherche/adhesions" },
            { "@type": "ListItem", position: 3, name: cityName.value, item: canonicalUrl },
          ],
        },
        ...(memberships.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: memberships.value.length,
            itemListElement: memberships.value.slice(0, 20).map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Offer",
                name: p.title,
                url: `https://donoor.org/a/${p.slug}`,
                price: Number(p.price).toFixed(2),
                priceCurrency: "EUR",
                seller: { "@type": "Organization", name: p.organization_name },
              },
            })),
          },
        }),
      }),
    },
  ]),
});
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Hero unifié (même UX que /adhesions) avec ville pré-filtrée -->
    <RechercheUnifiedHero
      active-type="adhesions"
      :h1="t('hero.h1', { ville: cityName })"
      :intro="t('hero.intro', { n: memberships.length, s: memberships.length > 1 ? 's' : '', ville: cityName })"
      :ville-slug="villeSlug"
      :ville-name="cityName"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
      <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div v-for="i in 6" :key="i" class="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
          <div class="h-44 bg-gray-200" />
          <div class="p-4 space-y-2">
            <div class="h-4 w-3/4 bg-gray-200 rounded" />
            <div class="h-3 w-1/2 bg-gray-100 rounded" />
          </div>
        </div>
      </div>

      <div v-else-if="memberships.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <NuxtLink
          v-for="p in memberships"
          :key="p.id"
          :to="`/a/${p.slug}`"
          :aria-label="t('carte.ariaLabel', { titre: p.title, organisation: p.organization_name, ville: cityName })"
          class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
        >
          <div class="relative h-44 overflow-hidden bg-gray-100">
            <img
              v-if="p.cover_image"
              :src="`${assetsUrl}/${p.cover_image}?width=600&height=400&fit=cover&format=webp&quality=80`"
              :alt="t('carte.alt', { titre: p.title })"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
              <UIcon name="i-lucide-id-card" class="size-10" />
            </div>
          </div>
          <div class="p-4">
            <div class="flex items-center gap-2 mb-2">
              <img
                v-if="p.organization_logo"
                :src="`${assetsUrl}/${p.organization_logo}?width=40&height=40&fit=contain&format=webp&quality=80`"
                :alt="t('carte.logo', { organisation: p.organization_name })"
                class="size-5 rounded-full object-contain bg-white border border-gray-100"
                loading="lazy"
              />
              <span class="text-xs text-gray-500 truncate">{{ p.organization_name }}</span>
            </div>
            <h2 class="font-semibold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-(--color-main) transition-colors">
              {{ p.title }}
            </h2>
            <div class="mt-3 flex items-center justify-between text-xs">
              <span class="font-semibold text-gray-900">
                {{ Number(p.price) === 0 ? t('carte.gratuit') : formatCurrency(Number(p.price)) }}
                <span v-if="p.duration_months" class="text-gray-400 font-normal">/ {{ formatDuration(p.duration_months) }}</span>
              </span>
            </div>
          </div>
        </NuxtLink>
      </div>

      <div v-else class="text-center py-16">
        <UIcon name="i-lucide-id-card" class="size-12 text-gray-300 mx-auto mb-4" />
        <p class="text-gray-500 mb-4">{{ t('vide.message', { ville: cityName }) }}</p>
        <NuxtLink to="/recherche/adhesions" class="text-sm text-(--color-main) hover:underline">
          {{ t('vide.lien') }}
        </NuxtLink>
      </div>

      <div v-if="data?.cities && data.cities.length > 1" class="mt-16 pt-12 border-t border-gray-100">
        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {{ t('autresVilles.titre') }}
        </h2>
        <p class="text-gray-500 text-sm mb-6">
          {{ t('autresVilles.intro') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="city in data.cities.filter((c) => c.slug !== villeSlug).slice(0, 30)"
            :key="city.slug"
            :to="`/adhesions/${city.slug}`"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 no-underline transition-colors"
          >
            <span>{{ city.name }}</span>
            <span class="text-gray-400">{{ city.count }}</span>
          </NuxtLink>
        </div>
      </div>

      <!-- Footer maillage interne SEO (discret, liens texte) -->
      <p class="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed">
        <span class="font-medium text-gray-700">{{ t('maillage.voirAussi', { ville: cityName }) }}</span>
        <NuxtLink :to="`/associations/ville/${villeSlug}`" class="text-(--color-main) hover:underline ml-1">{{ t('maillage.associations') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/cagnottes/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.cagnottes') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/evenements/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.evenements') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/boutiques/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.boutiques') }}</NuxtLink>
      </p>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/pages/adhesions/[ville].json"></i18n>
